import { CdkAccordionModule } from '@angular/cdk/accordion';
import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, input, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { TranslateService } from '@ngx-translate/core';
import { VadEagerness, VadType, VendorModel } from '@vendasta/ai-assistants';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { filter, map, of, startWith, switchMap } from 'rxjs';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { AiVoiceConfigurationForm, OpenAIRealtimeConfigForm } from '../../core/forms';
import type { AiVoiceFamily } from '../../core/interfaces/config.interface';
import { getVoiceFamilies } from '../../core/services/ai-assistant-utils';
import { AiAssistantService } from '../../core/services/ai-assistant.service';
import { AiVoiceSelectorTableComponent } from '../ai-voice-selector-table/ai-voice-selector-table.component';

@Component({
  selector: 'ai-voice-configuration-form',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    MatSelectModule,
    MatIconModule,
    MatSliderModule,
    MatRadioModule,
    MatCheckboxModule,
    MatInputModule,
    CdkAccordionModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    AiAssistantI18nModule,
    GalaxyBadgeModule,
    AiVoiceSelectorTableComponent,
  ],
  templateUrl: './ai-voice-configuration-form.component.html',
  styleUrl: './ai-voice-configuration-form.component.scss',
})
export class AiVoiceConfigurationFormComponent {
  readonly voiceConfigForm = input.required<AiVoiceConfigurationForm>();

  VendorModel = VendorModel;
  protected readonly VadType = VadType;
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly translateService = inject(TranslateService);

  protected readonly DEFAULT_PRE_GREETING_MESSAGE = 'This call is being recorded for quality and note taking purposes.';

  protected readonly voiceFamilies = toSignal(this.aiAssistantService.voiceFeatures$.pipe(map(getVoiceFamilies)));

  protected readonly voiceFamily = toSignal<AiVoiceFamily | null>(
    toObservable(this.voiceConfigForm).pipe(switchMap((form) => form.voiceFamily$)),
  );

  protected readonly vendorModel = computed(() => this.voiceFamily()?.provider);

  protected readonly modelConfig = computed(() => {
    return this.voiceConfigForm()?.getModelConfigForm(this.vendorModel() ?? null);
  });

  protected readonly openAITurnDetectionForm = computed(() => {
    const modelConfig = this.modelConfig();
    return this.vendorModel() === VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME &&
      modelConfig instanceof OpenAIRealtimeConfigForm
      ? modelConfig.controls.turnDetection
      : null;
  });

  private static readonly EAGERNESS_MAP = new Map<VadEagerness, string>([
    [VadEagerness.VAD_EAGERNESS_LOW, 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.TURN_DETECTION.EAGERNESS.LOW'],
    [VadEagerness.VAD_EAGERNESS_MEDIUM, 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.TURN_DETECTION.EAGERNESS.MEDIUM'],
    [VadEagerness.VAD_EAGERNESS_HIGH, 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.TURN_DETECTION.EAGERNESS.HIGH'],
  ]);

  protected eagernessDisplayFn = (value: VadEagerness): string => {
    const translationKey =
      AiVoiceConfigurationFormComponent.EAGERNESS_MAP.get(value) ??
      'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.TURN_DETECTION.EAGERNESS.MEDIUM';
    return this.translateService.instant(translationKey);
  };

  private readonly showPreGreetingMessage$ = toObservable(this.voiceConfigForm).pipe(
    filter((form) => !!form?.controls?.showPreGreetingMessage),
    switchMap((form) => {
      const showPreGreetingMessageControl = form?.controls?.showPreGreetingMessage;
      return (
        showPreGreetingMessageControl?.valueChanges.pipe(startWith(showPreGreetingMessageControl?.value)) ?? of(false)
      );
    }),
  );
  protected readonly showPreGreetingMessage = toSignal(this.showPreGreetingMessage$);

  constructor() {
    effect(() => {
      const voiceFamily = this.voiceFamily();

      untracked(() => {
        const voiceControl = this.voiceConfigForm()?.getVoiceControl(voiceFamily?.provider ?? null);

        if (voiceFamily?.voices && !voiceFamily.voices.some((aiVoice) => aiVoice.value === voiceControl?.value)) {
          const firstNonDeprecatedVoice = voiceFamily.voices.find(
            (v) =>
              !AiVoiceConfigurationForm.DEPRECATED_VOICES.some(
                (deprecated) => deprecated.provider === voiceFamily.provider && deprecated.voice === v.value,
              ),
          );
          const newValue = firstNonDeprecatedVoice?.value || voiceFamily.voices[0]?.value || '';

          if (newValue && newValue !== voiceControl?.value) {
            voiceControl?.setValue(newValue);
            voiceControl?.markAsDirty();
          }
        }

        const vendorModelControl = this.voiceConfigForm()?.controls?.vendorModel;
        if (vendorModelControl) {
          const newValue = voiceFamily?.provider ?? null;
          if (vendorModelControl.value !== newValue) {
            vendorModelControl.setValue(newValue);
            vendorModelControl.markAsDirty();
          }
        }
      });
    });
    effect(() => {
      const showPreGreetingMessage = this.showPreGreetingMessage();

      untracked(() => {
        const form = this.voiceConfigForm();
        const preGreetingMessageControl = form?.controls?.preGreetingMessage;

        if (showPreGreetingMessage && preGreetingMessageControl) {
          const currentValue = preGreetingMessageControl.value;
          if (!currentValue || currentValue.trim() === '') {
            preGreetingMessageControl.setValue(this.DEFAULT_PRE_GREETING_MESSAGE);
            preGreetingMessageControl.markAsDirty();
          }
        }
      });
    });
  }
}
